import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Trash2, Search, Ban } from 'lucide-react';

const STATUS_COLOR = { pending: '#c2410c', online: '#15803d', rejected: '#ef4444' };
const STATUS_LABEL = { pending: 'Wartet auf Freigabe', online: 'Online', rejected: 'Abgelehnt' };

export default function PinnwandTab() {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/app-verwaltung/pinboard', { params: { search: search || undefined } });
      setItems(r.data || []);
    } catch (e) { toast.error('Pinnwand konnte nicht geladen werden'); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const visible = items.filter((p) => !onlyPending || p.status === 'pending');

  const act = async (action) => {
    try {
      if (action === 'delete') {
        await api.delete(`/admin/app-verwaltung/pinboard/${sel.id}`);
        setSel(null);
      } else {
        const r = await api.post(`/admin/app-verwaltung/pinboard/${sel.id}/${action}`,
          action === 'reject' ? { reason: null } : {});
        setSel(r.data);
      }
      toast.success(action === 'publish' ? 'Freigegeben' : action === 'reject' ? 'Abgelehnt' : 'Gelöscht');
      load();
    } catch (e) { toast.error('Aktion fehlgeschlagen'); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="h-full flex">
      <div className="w-96 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-3 space-y-2 border-b border-slate-100">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} data-testid="pinnwand-filter-pending" /> Nur freizugebende anzeigen
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Schnellsuche" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pinnwand-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {visible.map((p) => (
            <button key={p.id} onClick={() => setSel(p)}
                    data-testid={`pinnwand-item-${p.id}`}
                    className={`w-full text-left flex items-stretch border-b border-slate-100 ${sel?.id === p.id ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
              <span className="w-1.5 shrink-0" style={{ background: STATUS_COLOR[p.status] }} />
              <span className="flex-1 min-w-0 px-3 py-2.5">
                <span className="flex justify-between text-xs text-slate-400"><span>{p.author_name}</span><span>{fmt(p.created_at)}</span></span>
                <span className="block text-sm font-medium truncate">{p.title}</span>
                <span className="block text-xs text-slate-400 truncate">{p.body}</span>
              </span>
            </button>
          ))}
          {!visible.length && <p className="p-6 text-center text-sm text-slate-400">Keine Beiträge</p>}
        </div>
        <p className="p-2 text-[11px] text-slate-400 border-t border-slate-100">Gefunden: {visible.length} / Gesamt: {items.length}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {sel ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-2xl" data-testid="pinnwand-detail">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Von {sel.author_name}</p>
            <h2 className="text-lg font-bold mb-4">{sel.title}</h2>
            <p className="text-sm whitespace-pre-wrap text-slate-700">{sel.body}</p>
          </div>
        ) : (
          <p className="text-slate-400 text-sm mt-20 text-center">Beitrag links auswählen — neue Beiträge kommen von den App-Nutzern</p>
        )}
      </div>

      {sel && (
        <div className="w-64 shrink-0 border-l border-slate-200 bg-white p-4 space-y-3">
          {sel.status !== 'online' && (
            <Button onClick={() => act('publish')} className="w-full bg-brand-red hover:bg-brand-red/90" data-testid="pinnwand-publish-btn">
              <Eye className="w-4 h-4 mr-1" /> Freigeben
            </Button>
          )}
          {sel.status === 'online' && (
            <Button onClick={() => act('reject')} variant="outline" className="w-full" data-testid="pinnwand-offline-btn">
              <EyeOff className="w-4 h-4 mr-1" /> Offline nehmen
            </Button>
          )}
          {sel.status === 'pending' && (
            <Button onClick={() => act('reject')} variant="outline" className="w-full" data-testid="pinnwand-reject-btn">
              <Ban className="w-4 h-4 mr-1" /> Ablehnen
            </Button>
          )}
          <div className="text-sm space-y-3 pt-1">
            <div><p className="text-xs text-slate-400">Status</p>
              <p className="font-semibold" style={{ color: STATUS_COLOR[sel.status] }}>{STATUS_LABEL[sel.status]}</p></div>
            <div><p className="text-xs text-slate-400">Autor:in</p><p>{sel.author_name}</p></div>
            <div><p className="text-xs text-slate-400">Eingereicht</p><p>{fmt(sel.created_at)}</p></div>
            {sel.published_at && <div><p className="text-xs text-slate-400">Veröffentlicht</p><p>{fmt(sel.published_at)}</p></div>}
          </div>
          <button onClick={() => act('delete')} className="text-rose-500 text-sm flex items-center gap-1" data-testid="pinnwand-delete-btn"><Trash2 className="w-4 h-4" /> Löschen</button>
        </div>
      )}
    </div>
  );
}
